import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Alert } from '@mm/components';
import { useQuery } from '@mm/hooks';
import declinedContactsCRUDActions from '@mm/state/actions/DeclinedContactsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { ColorType } from '@mm/utils';
import React, { useEffect, useRef, useState } from 'react';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../common/buttons/PrimaryButton/PrimaryButton';
import CheckBox from '../../../common/components/Checkbox';
import TextInput from '../../../common/components/TextInput';
import {
  EmailInput,
  useEmailInput,
} from '../../../common/modals/InviteUserModal/EmailInput/EmailInput';

import styles from './OptOutForm.module.css';

export const OptOutForm = () => {
  const [zipCode, setZipCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [agentCheck, setAgentCheck] = useState(false);
  const { emailState, onChange } = useEmailInput();
  const [alertContent, setAlertContent] = useState('');
  const [alertType, setAlertType] = useState<ColorType | undefined>();
  const hCaptchaRef = useRef<HCaptcha>(null);
  const { openNewInfoModal } = useModalActions();
  const query = useQuery();

  useEffect(() => {
    const email = query.get('email');
    const token = query.get('token');

    const confirmOptOut = async (email: string, token: string) => {
      const response = await declinedContactsCRUDActions.confirm(email, token);

      if (response.success) {
        openNewInfoModal({
          title: 'Please confirm your Opt-Out',
          description: (
            <div>
              <p>
                Your information has been submitted and will be reviewed by our
                legal team for compliance.
              </p>
              <p>This may take up to 30 days to complete.</p>
            </div>
          ),
          button: 'Ok',
        });
      } else {
        openNewInfoModal({
          title: 'Oops, Something went wrong',
          description: response.errors.join(' '),
          button: 'Ok',
        });
      }
    };

    if (token && email) {
      confirmOptOut(email, token);
    }
  }, []);

  const resetCaptcha = () => {
    hCaptchaRef.current?.resetCaptcha();
    setCaptchaToken('');
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (emailState.isValid) {
      const { email } = emailState;
      const response = await declinedContactsCRUDActions.create(
        email,
        captchaToken,
        zipCode,
        agentCheck,
      );

      const warningErrorCodes = ['already_confirmed', 'already_sent'];

      setAlertType(
        response.success
          ? 'success'
          : warningErrorCodes.includes(response?.error_code)
          ? 'warning'
          : 'error',
      );

      if (response.success) {
        openNewInfoModal({
          title: 'Opt-Out Confirmed',
          description: 'Your information has been submitted',
          button: 'Ok',
        });
      } else {
        setAlertContent(
          'Something went wrong. Please try again later.  If the problem persists, please contact support@customers.ai for assistance.',
        );
      }

      resetCaptcha();
      setTimeout(() => {
        setAlertContent('');
      }, 5000);
    }
  };

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Email Address</label>
        <EmailInput
          onChange={onChange}
          inputProps={{
            className: styles.inputTextField,
            placeholder: '',
          }}
          withIcon={false}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Zip Code</label>
        <TextInput
          className={styles.inputTextField}
          placeholder=""
          aria-label="text"
          onChange={e => setZipCode(e.target.value)}
          type="text"
          style={{ border: '1px solid var(--color-gray-5)' }}
        />
      </div>

      <div className={styles.inputGroup}>
        <CheckBox
          isChecked={agentCheck}
          label="I am an authorized agent submitting the request on behalf of the user."
          checkboxLabelTitleClassName={styles.checkboxLabel}
          onToggle={e => setAgentCheck(e.target.checked)}
        />
      </div>

      <div className={styles.inputGroup}>
        <HCaptcha
          ref={hCaptchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={token => setCaptchaToken(token)}
        />
      </div>

      <div className={styles.inputGroup}>
        <PrimaryButton
          disabled={!captchaToken || !emailState.isValid}
          className={styles.inputSubmit}
          color={ButtonColor.blue}
          type="submit"
        >
          Opt-Out
        </PrimaryButton>
      </div>
      {alertContent && (
        <Alert type={alertType} showCloseIcon={false}>
          {alertContent}
        </Alert>
      )}
    </form>
  );
};
